import { useState, useMemo } from 'react';
import { motion } from 'motion/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  BookOpen,
  Clock,
  Search,
  ArrowRight,
  TrendingUp,
  Home,
  DollarSign,
  Paintbrush,
  BarChart3,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Types ──────────────────────────────────────────────
type BlogCategory =
  | 'All Posts'
  | 'Market Trends'
  | 'Buying Guide'
  | 'Selling Tips'
  | 'Home Improvement'
  | 'Investment';

interface BlogSection {
  heading?: string;
  paragraphs: string[];
}

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  category: BlogCategory;
  image: string;
  imageAlt: string;
  author: string;
  date: string;
  readTime: number;
  featured?: boolean;
  body: BlogSection[];
}

// ── Category config ────────────────────────────────────
const CATEGORY_ICONS: Record<Exclude<BlogCategory, 'All Posts'>, React.ElementType> = {
  'Market Trends': TrendingUp,
  'Buying Guide': Home,
  'Selling Tips': DollarSign,
  'Home Improvement': Paintbrush,
  Investment: BarChart3,
};

const CATEGORIES: BlogCategory[] = [
  'All Posts',
  'Market Trends',
  'Buying Guide',
  'Selling Tips',
  'Home Improvement',
  'Investment',
];

// ── Mock blog data ─────────────────────────────────────
const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: '5 Tips for First-Time Home Buyers in South Africa',
    excerpt:
      'Navigate the home buying process with confidence using our expert guide covering everything from pre-approval to transfer.',
    category: 'Buying Guide',
    image: 'https://images.pexels.com/photos/7641900/pexels-photo-7641900.jpeg',
    imageAlt: 'Couple receiving keys to new home – Photo by Alena Darmel on Pexels',
    author: 'Something Property Team',
    date: '2026-03-15',
    readTime: 6,
    featured: true,
    body: [
      {
        paragraphs: [
          'Buying your first home is one of the most significant financial decisions you will ever make. The South African property market offers incredible opportunities, but the process can feel overwhelming without the right guidance. From securing a home loan to signing at the attorney\'s office, here is everything you need to know to navigate your first purchase with confidence.',
        ],
      },
      {
        heading: '1. Get Pre-Approved Before You Start Viewing',
        paragraphs: [
          'Before falling in love with any property, visit your bank or a bond originator to get a home loan pre-approval. Pre-approval tells you exactly what you can afford, strengthens your offer in a competitive market, and speeds up the formal approval process once you have found your home.',
          'A bond originator such as ooba or BetterBond submits your application to multiple banks simultaneously at no cost to you. This comparison can save you tens of thousands of rands over the loan term. Most pre-approvals are valid for 90 days, giving you time to shop without pressure.',
        ],
      },
      {
        heading: '2. Budget for All the Costs — Not Just the Purchase Price',
        paragraphs: [
          'First-time buyers routinely underestimate the upfront costs of buying property. Beyond the purchase price you need to budget for transfer duty (for properties above R1 100 000), conveyancing attorney fees, bond registration attorney fees, rates and levy clearance certificates, and an occupational rental if you move in before registration.',
          'As a rough guide, set aside 8–10% of the purchase price to cover all transfer and registration costs. Your conveyancing attorney will provide a detailed cost estimate once an offer to purchase has been accepted.',
        ],
      },
      {
        heading: '3. Understand the Offer to Purchase',
        paragraphs: [
          'The Offer to Purchase (OTP) is a legally binding contract. Read every clause before signing, and do not be afraid to ask your agent to explain anything you do not understand. Pay particular attention to suspensive conditions (such as bond approval within a specified timeframe), the voetstoets clause and any exclusions, fixtures and fittings listed as included or excluded, and occupation date versus registration date.',
          'Once both parties have signed and all suspensive conditions have been met, the OTP becomes a deed of sale and cannot easily be cancelled without financial consequences.',
        ],
      },
      {
        heading: '4. Choose a Registered and Reputable Agent',
        paragraphs: [
          'Your estate agent should be registered with the Property Practitioners Regulatory Authority (PPRA) and hold a valid Fidelity Fund Certificate (FFC). Ask to see their FFC before signing any mandate. A good agent will guide you through every step, negotiate on your behalf, and ensure all disclosures are made in writing.',
          'Something Property agents are all PPRA-registered and trained in the full transfer process — from the initial viewing to the day you receive your keys.',
        ],
      },
      {
        heading: '5. Plan for Life After Transfer',
        paragraphs: [
          'Many first-time buyers are so focused on getting the keys that they forget to budget for what comes next: moving costs, initial repairs or repainting, connecting utilities, home insurance (required by your lender), and the first month\'s bond repayment. Build a post-transfer buffer of at least one to two months\' bond repayment into your savings before you commit to a purchase price.',
          'Buying your first home should be exciting, not stressful. With the right preparation and the right team behind you, you will be handing over the cheque and collecting your keys before you know it.',
        ],
      },
    ],
  },
  {
    id: '2',
    title: 'Understanding Transfer Duty and Costs When Buying Property',
    excerpt:
      'Learn about transfer duty thresholds, attorney fees, and hidden costs to budget accurately for your property purchase.',
    category: 'Buying Guide',
    image: 'https://images.pexels.com/photos/7651738/pexels-photo-7651738.jpeg',
    imageAlt: 'Financial documents on desk – Photo by Kindel Media on Pexels',
    author: 'Something Property Team',
    date: '2026-03-10',
    readTime: 8,
    body: [
      {
        paragraphs: [
          'One of the most common surprises for property buyers in South Africa is the bill that arrives from the conveyancing attorney before registration. Transfer duty, bond registration fees, and a handful of smaller charges can add up to a significant sum. Understanding exactly what you will pay — and when — is essential for accurate budgeting.',
        ],
      },
      {
        heading: 'What Is Transfer Duty?',
        paragraphs: [
          'Transfer duty is a government tax levied on the acquisition of immovable property. In 2026 the thresholds are as follows: properties up to R1 100 000 are exempt (0%). Properties between R1 100 001 and R1 512 500 attract 3% on the value above R1 100 000. Properties between R1 512 501 and R2 117 500 attract R12 375 plus 6% on the excess. Properties between R2 117 501 and R2 722 500 attract R49 125 plus 8% on the excess. Properties between R2 722 501 and R12 100 000 attract R97 525 plus 11% on the excess. Properties above R12 100 000 attract R1 128 600 plus 13% on the excess.',
          'Transfer duty is payable by the buyer and must be paid to SARS within six months of the date of the sale agreement. Your conveyancing attorney handles this payment on your behalf.',
        ],
      },
      {
        heading: 'Conveyancing Attorney Fees',
        paragraphs: [
          'The seller appoints the conveyancing attorney, but the buyer pays their fees. Conveyancing fees are regulated by the Law Society and are calculated on a sliding scale based on the purchase price. For a property priced at R2 000 000, expect conveyancing fees in the region of R28 000–R35 000 including VAT and disbursements.',
          'Disbursements include deeds office fees, postage and petties, electronic document management fees, and FICA compliance costs. Always ask for a detailed cost estimate upfront so there are no surprises at registration.',
        ],
      },
      {
        heading: 'Bond Registration Fees',
        paragraphs: [
          'If you are financing your purchase with a home loan, the bank\'s bond must be registered at the Deeds Office. Your bank will appoint a bond registration attorney — separate from the transfer attorney — and you pay their fees as well.',
          'Bond registration fees are also calculated on a sliding scale. For a R1 800 000 bond, expect fees of approximately R22 000–R28 000 including VAT. Some banks offer to pay a portion of these fees as an incentive — always ask your bank what they will cover before you finalise your loan.',
        ],
      },
      {
        heading: 'Other Costs to Factor In',
        paragraphs: [
          'Beyond transfer duty and attorney fees, budget for: a rates clearance certificate (R500–R1 500 depending on the municipality), a levy clearance certificate for sectional title schemes (R500–R2 000), and an electrical compliance certificate (typically paid by the seller but sometimes negotiated between the parties).',
          'A useful rule of thumb: budget 8–10% of the purchase price to cover all transfer-related costs comfortably. This gives you a buffer for unexpected disbursements and avoids delays at registration.',
        ],
      },
      {
        heading: 'VAT vs Transfer Duty',
        paragraphs: [
          'If you are buying a brand-new property directly from a developer who is a registered VAT vendor, the purchase price will include VAT at 15% and no transfer duty is payable. This is an important distinction when comparing new developments to existing homes — make sure you are comparing apples with apples and factor in whether VAT is included in the advertised price.',
        ],
      },
    ],
  },
  {
    id: '3',
    title: 'Top Home Renovation Ideas That Add Value to Your Property',
    excerpt:
      'Discover the renovation projects that offer the best return on investment when it\'s time to sell your home.',
    category: 'Home Improvement',
    image: 'https://images.pexels.com/photos/4469165/pexels-photo-4469165.jpeg',
    imageAlt: 'Modern kitchen renovation – Photo by Curtis Adams on Pexels',
    author: 'Something Property Team',
    date: '2026-03-05',
    readTime: 5,
    body: [
      {
        paragraphs: [
          'Not all renovations are created equal. Some projects add rand-for-rand value to your home while others simply make it more comfortable to live in without moving the needle on resale price. If you are renovating with an eye on resale, focus on the improvements that buyers consistently rank highest.',
        ],
      },
      {
        heading: '1. Kitchen Upgrade',
        paragraphs: [
          'The kitchen is the single highest-return room in any home. You do not need a complete gut renovation to make an impact. Replacing cabinet fronts, upgrading countertops to quartz or granite, installing a new sink and mixer, and adding modern integrated appliances can transform a dated kitchen for R40 000–R80 000 and add significantly more to your asking price.',
          'Buyers pay a premium for open-plan kitchens that flow into the living area. If your layout allows, removing a non-structural wall between the kitchen and lounge is one of the most effective investments you can make.',
        ],
      },
      {
        heading: '2. Bathroom Refresh',
        paragraphs: [
          'Bathrooms rank second only to kitchens in buyer priority. A full retile and refit with a new vanity, frameless shower, and quality tapware typically costs R25 000–R60 000 per bathroom and adds disproportionate value in the buyer\'s perception of the home.',
          'At minimum, replace dated fittings, re-grout tiles, and ensure all fixtures are in working order. A sparkling clean bathroom with fresh white grout reads as "well maintained" to every buyer who walks through the door.',
        ],
      },
      {
        heading: '3. Curb Appeal and Landscaping',
        paragraphs: [
          'First impressions are formed before a buyer steps inside. A freshly painted exterior, maintained garden, clean driveway, and modern front door can increase your listing price by 5–10% according to local agent data. These are often low-cost, high-impact improvements.',
          'Consider repainting your boundary wall, upgrading outdoor lighting, and planting low-maintenance indigenous plants that look great year-round without driving up your water bill.',
        ],
      },
      {
        heading: '4. Entertainment Areas',
        paragraphs: [
          'South Africans value outdoor living. A covered patio with a built-in braai, quality decking, and a well-finished entertainment area is one of the most sought-after features in the Gauteng market. Budget R50 000–R120 000 for a well-finished entertainment area and expect buyers to factor it heavily into their offer.',
        ],
      },
      {
        heading: '5. Energy Efficiency',
        paragraphs: [
          'With load shedding and rising electricity costs, solar panels and inverter systems have moved from luxury to near-essential in the South African buyer\'s checklist. A solar PV system with battery backup typically costs R80 000–R150 000 to install and is increasingly expected in mid-to-upper market homes.',
          'Even smaller energy improvements such as LED lighting throughout, heat pumps, and smart geysers add perceived value and reduce utility costs for the next owner — a strong selling point in your listing that agents can highlight to buyers.',
        ],
      },
    ],
  },
  {
    id: '4',
    title: 'The State of the Gauteng Property Market in 2026',
    excerpt:
      'An in-depth look at current market trends, pricing dynamics, and opportunities across Johannesburg and Pretoria.',
    category: 'Market Trends',
    image: 'https://images.pexels.com/photos/5997758/pexels-photo-5997758.jpeg',
    imageAlt: 'Aerial view of residential neighbourhood – Photo by Deane Bayas on Pexels',
    author: 'Something Property Team',
    date: '2026-02-28',
    readTime: 10,
    body: [
      {
        paragraphs: [
          'After several years of rate uncertainty and post-pandemic recalibration, the Gauteng property market entered 2026 with cautious optimism. The South African Reserve Bank\'s rate-cutting cycle that began in late 2024 has materially improved affordability, and transaction volumes across Johannesburg and Pretoria are tracking ahead of the same period in 2025.',
        ],
      },
      {
        heading: 'Interest Rates and Affordability',
        paragraphs: [
          'The prime lending rate as of early 2026 sits at 11.25%, down from the cycle peak of 11.75% in 2023. For a buyer with a R2 000 000 bond over 20 years, the cumulative effect of the rate cuts represents a monthly saving of approximately R850 compared to peak. This has unlocked a meaningful cohort of buyers who were previously priced out of the market.',
          'Economists expect one to two further 25-basis-point cuts through 2026, which would bring prime to 10.75%–11%. Any further easing will provide additional tailwind for transaction volumes in the second half of the year.',
        ],
      },
      {
        heading: 'Price Growth by Segment',
        paragraphs: [
          'The sectional title market in Gauteng has outperformed freehold for the third consecutive year. The median price of sectional title units in Johannesburg\'s northern suburbs grew 6.2% year-on-year to March 2026, driven by strong demand from first-time buyers and semigration from higher-priced Cape Town.',
          'Freehold properties in the R3 000 000–R6 000 000 band have seen more moderate growth of 3.8%, constrained by affordability in the upper-middle market. The luxury segment above R10 000 000 remains subdued with extended days-on-market compared to previous cycles.',
        ],
      },
      {
        heading: 'Hotspot Suburbs to Watch',
        paragraphs: [
          'In Johannesburg, Fourways and the broader Sandton corridor continue to attract young professionals and families. Midrand has emerged as a value proposition for buyers priced out of Bryanston and Morningside, with new sectional title developments selling strongly.',
          'In Pretoria, Menlyn, Garsfontein, and Waterkloof Ridge are leading in both volume and price growth. The ongoing expansion of the Menlyn Maine precinct has spurred significant investor interest in the surrounding suburbs. On the East Rand, Edenvale and Bedfordview represent attractive value relative to comparable West Rand suburbs and are drawing increasing interest from both owner-occupiers and buy-to-let investors.',
        ],
      },
      {
        heading: 'Rental Market Dynamics',
        paragraphs: [
          'Rental demand remains robust across Gauteng as high bond deposit requirements keep many households in the rental market longer. Average gross rental yields for well-located sectional title units range from 7.5% to 9.2%, making Gauteng one of the more attractive yield markets in the country.',
          'Vacancy rates in well-managed complexes in Fourways, Centurion, and Midrand are below 5%, and rental escalations of 8–10% on renewals are being achieved by landlords with quality stock in high-demand nodes.',
        ],
      },
      {
        heading: 'Outlook for the Rest of 2026',
        paragraphs: [
          'The consensus view among Gauteng agents and economists is that the market will continue to improve gradually through 2026. Key risks include political and policy uncertainty, persistent load shedding dampening consumer sentiment, and the potential for global rate movements to influence the SARB\'s hand.',
          'For buyers, the window of relatively attractive rates and motivated sellers remains open. For sellers, correctly pricing your property in line with current comparable sales is essential — overpriced stock is sitting significantly longer than correctly priced stock in every suburb across the metro.',
        ],
      },
    ],
  },
  {
    id: '5',
    title: 'How to Choose the Right Neighbourhood for Your Family',
    excerpt:
      'Key factors to consider when selecting a suburb including schools, safety, amenities, and future development plans.',
    category: 'Buying Guide',
    image: 'https://images.pexels.com/photos/5178040/pexels-photo-5178040.jpeg',
    imageAlt: 'Suburban neighbourhood aerial view – Photo by Curtis Adams on Pexels',
    author: 'Something Property Team',
    date: '2026-02-20',
    readTime: 7,
    body: [
      {
        paragraphs: [
          'Choosing the right suburb is arguably more important than choosing the right house. You can renovate a home, but you cannot move the neighbourhood. Whether you are relocating with school-age children, downsizing as an empty-nester, or buying your first home, the suburb you choose will shape your daily life for years to come.',
        ],
      },
      {
        heading: 'School Zones and Education Quality',
        paragraphs: [
          'For families with children, school proximity and quality are typically the primary driver of suburb selection. Research the catchment areas for government schools and the proximity of quality independent schools before committing to an area. In Gauteng, suburbs such as Bryanston, Sandton, Randpark Ridge, and Waterkloof are perennially popular precisely because of their access to well-regarded schools.',
          'Note that school placement policies change, and catchment areas are not always guaranteed. Contact the school directly to confirm registration requirements before assuming that proximity guarantees placement.',
        ],
      },
      {
        heading: 'Safety and Security Profile',
        paragraphs: [
          'Safety is a non-negotiable for most South African families. Research the suburb\'s crime statistics on the SAPS community crime statistics portal, speak to current residents, and assess the security infrastructure: boomed roads, active neighbourhood watches, CCTV coverage, and armed response density.',
          'Look for suburbs with active ratepayers\' associations and community engagement forums. A well-organised community is a strong predictor of long-term security investment and property value stability.',
        ],
      },
      {
        heading: 'Amenities and Lifestyle Fit',
        paragraphs: [
          'Consider which amenities matter most to your household: proximity to quality healthcare (hospitals and GPs), shopping centres, gyms, restaurants, and recreational facilities such as parks and sports clubs. A 15-minute drive to everything you need is very different from a 45-minute commute in Gauteng traffic.',
          'Visit the suburb at different times of day and on different days of the week. A neighbourhood that feels ideal on a quiet Tuesday morning may feel very different during school drop-off or weekend traffic.',
        ],
      },
      {
        heading: 'Transport and Commute',
        paragraphs: [
          'Map your daily commute from each suburb under consideration using realistic peak-hour conditions. Gauteng traffic patterns mean that seemingly close suburbs can represent very different commute realities. Access to the Gautrain network is a premium that is reflected in property prices around Rosebank, Sandton, Midrand, and Centurion stations, but for many commuters the time savings justify the higher entry price.',
        ],
      },
      {
        heading: 'Future Development and Infrastructure Plans',
        paragraphs: [
          'Research approved developments, rezoning applications, and infrastructure plans in the suburb and its surrounds. A new major development can be a positive (new retail, improved roads, increased employment) or a negative (increased traffic, changed character of the suburb). The Johannesburg City Planning portal and local municipal websites publish approved development plans.',
          'Suburbs adjacent to major new infrastructure projects — such as freeway upgrades or new commercial nodes — have historically delivered above-market capital growth in the years following completion.',
        ],
      },
      {
        heading: 'Making the Final Decision',
        paragraphs: [
          'Once you have shortlisted two or three suburbs, spend time there. Walk the streets, visit the local shops, talk to residents, and attend an open house or two. The numbers tell part of the story but the feel of a suburb is ultimately personal. Trust both the data and your instincts — and lean on your Something Property agent\'s local knowledge to fill in the gaps.',
        ],
      },
    ],
  },
  {
    id: '6',
    title: 'Property Investment: Building Wealth Through Real Estate',
    excerpt:
      'Explore different property investment strategies and how to build a diversified portfolio in the South African market.',
    category: 'Investment',
    image: 'https://images.pexels.com/photos/7580852/pexels-photo-7580852.jpeg',
    imageAlt: 'Calculator on financial documents – Photo by RDNE Stock project on Pexels',
    author: 'Something Property Team',
    date: '2026-02-15',
    readTime: 9,
    body: [
      {
        paragraphs: [
          'Real estate has historically been one of the most reliable vehicles for wealth creation in South Africa. Unlike equities, property is a tangible asset that you can improve, leverage, and generate income from simultaneously. Whether you are looking at your first investment property or building a diversified portfolio, understanding the fundamentals will help you make decisions that compound over time.',
        ],
      },
      {
        heading: 'Buy-to-Let: The Foundation of Most Portfolios',
        paragraphs: [
          'The most accessible entry point into property investment is a buy-to-let property — purchasing a residential unit and renting it out to generate monthly income. The key metrics to evaluate are the gross rental yield (annual rent divided by purchase price), the net yield after costs (rates, levies, maintenance, insurance, management fees), and the loan-to-value ratio that determines your monthly bond repayment.',
          'In Gauteng, targeting a gross yield of 8% or above on sectional title properties in well-managed complexes provides a reasonable buffer to remain cashflow neutral or slightly positive from day one. Sub-7% yields require a longer-term capital growth thesis to justify the investment.',
        ],
      },
      {
        heading: 'The Power of Leverage',
        paragraphs: [
          'One of property\'s unique advantages is the ability to use the bank\'s money to control a large asset. A R2 500 000 property purchased with a 10% deposit (R250 000) generates returns on the full asset value while you have only deployed R250 000 of your own capital. If the property appreciates 5% in year one, you have made R125 000 on a R250 000 investment — a 50% return on equity.',
          'Leverage amplifies both gains and losses, which is why property selection, location, and tenant quality matter enormously. A well-located property with quality tenants is largely self-financing; a poor location with frequent vacancy can quickly become a financial burden rather than a wealth-building asset.',
        ],
      },
      {
        heading: 'Capital Growth vs Rental Income',
        paragraphs: [
          'Some investors prioritise rental yield (income now) while others prioritise capital growth (wealth later). In practice, the best investments deliver both, but understanding your objective shapes your buying decisions.',
          'For capital growth, focus on suburbs with strong demand fundamentals: population growth, employment hubs, good schools, and limited new supply. For yield, focus on smaller sectional title units — one- and two-bedroom apartments — in well-managed complexes near public transport or major employment nodes.',
        ],
      },
      {
        heading: 'Sectional Title vs Freehold Investment',
        paragraphs: [
          'Sectional title (apartments and townhouses in schemes) offers lower entry prices, reduced maintenance responsibility, and typically stronger rental yields. The trade-off is monthly levies and the risk of a poorly managed body corporate with insufficient maintenance reserves.',
          'Freehold properties offer full control, more potential to add value through improvements, and stronger capital growth in the right location. They require more active management and typically deliver lower initial yields but can be more impactful in the long run for a growing portfolio.',
        ],
      },
      {
        heading: 'Building a Diversified Portfolio',
        paragraphs: [
          'Seasoned property investors rarely concentrate their capital in a single property or location. A diversified portfolio might include a mix of sectional title buy-to-let units in Gauteng for yield, a freehold property in a growth suburb for capital appreciation, and exposure to commercial or industrial property through a REIT (Real Estate Investment Trust) listed on the JSE for liquidity.',
          'REITs provide exposure to large-scale commercial, retail, and industrial property portfolios without the management burden of direct ownership. They are a useful complement to direct property investment and can be held alongside it in a balanced long-term wealth strategy.',
        ],
      },
      {
        heading: 'Tax Considerations',
        paragraphs: [
          'Rental income is taxable at your marginal income tax rate, but allowable deductions include bond interest, rates and taxes, levies, insurance, repairs and maintenance, and depreciation on improvements. If you sell an investment property, capital gains tax applies at an inclusion rate of 40% for individuals, taxed at your marginal rate.',
          'Structuring your investment correctly — whether in your personal name, a trust, or a company — has significant long-term tax implications. Consult a qualified tax practitioner before purchasing your first investment property to ensure your structure aligns with your broader estate and financial planning.',
        ],
      },
      {
        heading: 'Getting Started',
        paragraphs: [
          'The best time to start building a property portfolio is when you are financially ready: a stable income, a manageable debt-to-income ratio, a deposit saved, and an emergency fund to cover vacancy and unexpected maintenance. Start with one quality property in a location you understand, manage it well, and reinvest the equity into your next acquisition.',
          'Something Property advisors can help you analyse potential investment properties, understand local rental demand, and connect you with the right bond originators and attorneys to execute your first acquisition efficiently.',
        ],
      },
    ],
  },
];

// ── Helpers ────────────────────────────────────────────
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ── Blog Post Detail ────────────────────────────────────
function BlogPostDetail({
  post,
  onBack,
}: {
  post: BlogPost;
  onBack: () => void;
}) {
  const CatIcon =
    CATEGORY_ICONS[post.category as Exclude<BlogCategory, 'All Posts'>] ?? BookOpen;

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero image */}
      <div className="relative h-72 md:h-96 overflow-hidden">
        <img
          src={post.image}
          alt={post.imageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <Badge
            variant="outline"
            className="mb-3 bg-background/80 backdrop-blur-sm border-primary/30 text-primary"
          >
            <CatIcon className="w-3 h-3 mr-1" />
            {post.category}
          </Badge>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight max-w-3xl">
            {post.title}
          </h1>
        </div>
      </div>

      {/* Article body */}
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="mb-8 -ml-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
          Back to Blog
        </Button>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-10 pb-6 border-b border-border/50">
          <span>By {post.author}</span>
          <span>•</span>
          <span>{formatDate(post.date)}</span>
          <span>•</span>
          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            <span>{post.readTime} min read</span>
          </div>
        </div>

        {/* Sections */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {post.body.map((section, i) => (
            <div key={i}>
              {section.heading && (
                <h2 className="text-xl md:text-2xl font-semibold mb-3 text-foreground">
                  {section.heading}
                </h2>
              )}
              {section.paragraphs.map((p, j) => (
                <p
                  key={j}
                  className="text-base text-muted-foreground leading-relaxed mb-3 last:mb-0"
                >
                  {p}
                </p>
              ))}
            </div>
          ))}
        </motion.div>

        {/* Back button (bottom) */}
        <div className="mt-14 pt-8 border-t border-border/50">
          <Button variant="outline" onClick={onBack}>
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
            Back to all articles
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Component ──────────────────────────────────────────
export function Blog() {
  const [activeCategory, setActiveCategory] = useState<BlogCategory>('All Posts');
  const [searchQuery, setSearchQuery] = useState('');
  const [email, setEmail] = useState('');
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  const filteredPosts = useMemo(() => {
    let posts = blogPosts;
    if (activeCategory !== 'All Posts') {
      posts = posts.filter((p) => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      posts = posts.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q)
      );
    }
    return posts;
  }, [activeCategory, searchQuery]);

  const featuredPost = blogPosts.find((p) => p.featured);
  const gridPosts = filteredPosts.filter((p) => !p.featured || activeCategory !== 'All Posts' || searchQuery.trim());

  if (selectedPost) {
    return (
      <BlogPostDetail
        post={selectedPost}
        onBack={() => setSelectedPost(null)}
      />
    );
  }

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    toast.success('Subscribed!', {
      description: 'You\'ll receive our latest property insights in your inbox.',
    });
    setEmail('');
  };

  const handleReadMore = (post: BlogPost) => {
    setSelectedPost(post);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen overflow-hidden">
      {/* ─── Hero Section ─── */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(88,101,242,0.08),transparent_50%)]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-10"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6"
            >
              <BookOpen className="w-4 h-4 text-primary" />
              <span className="text-sm">Property Blog</span>
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
              Latest Insights &amp; Tips
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-10">
              Stay informed with the latest property market trends, buying
              guides, and expert advice from Something Property.
            </p>

            {/* Search */}
            <div className="max-w-md mx-auto relative mb-8">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category pills */}
            <div className="flex flex-wrap justify-center gap-2">
              {CATEGORIES.map((cat) => (
                <Button
                  key={cat}
                  size="sm"
                  variant={activeCategory === cat ? 'default' : 'outline'}
                  onClick={() => setActiveCategory(cat)}
                  className={
                    activeCategory === cat
                      ? 'bg-primary text-primary-foreground'
                      : 'border-border/50 hover:border-primary/50 hover:bg-primary/5'
                  }
                >
                  {cat}
                </Button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Featured Article ─── */}
      {featuredPost && activeCategory === 'All Posts' && !searchQuery.trim() && (
        <section className="py-4 relative">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Card
                className="overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 cursor-pointer group"
                onClick={() => handleReadMore(featuredPost)}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {/* Image */}
                  <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                    <img
                      src={featuredPost.image}
                      alt={featuredPost.imageAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground border-0 shadow-lg">
                      Featured
                    </Badge>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col justify-center p-8 lg:p-12">
                    <Badge
                      variant="outline"
                      className="w-fit mb-4 border-primary/30 text-primary"
                    >
                      {featuredPost.category}
                    </Badge>
                    <h2 className="text-2xl lg:text-3xl font-bold mb-4 group-hover:text-primary transition-colors">
                      {featuredPost.title}
                    </h2>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                      <span>By {featuredPost.author}</span>
                      <span>•</span>
                      <span>{formatDate(featuredPost.date)}</span>
                      <span>•</span>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{featuredPost.readTime} min read</span>
                      </div>
                    </div>
                    <Button className="w-fit group/btn" variant="default">
                      Read More
                      <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </section>
      )}

      {/* ─── Article Grid ─── */}
      <section className="py-16 relative">
        <div className="container mx-auto px-4">
          {gridPosts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <BookOpen className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-xl text-muted-foreground">
                No articles found. Try a different category or search term.
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {gridPosts.map((post, index) => {
                const CatIcon =
                  CATEGORY_ICONS[post.category as Exclude<BlogCategory, 'All Posts'>] ?? BookOpen;
                return (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card
                      className="h-full border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group cursor-pointer overflow-hidden flex flex-col"
                      onClick={() => handleReadMore(post)}
                    >
                      {/* Image */}
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.imageAlt}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <Badge
                          variant="outline"
                          className="absolute top-3 left-3 bg-background/80 backdrop-blur-sm border-border/50"
                        >
                          <CatIcon className="w-3 h-3 mr-1" />
                          {post.category}
                        </Badge>
                      </div>

                      {/* Content */}
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg leading-snug group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex-1 flex flex-col">
                        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4">
                          {post.excerpt}
                        </p>
                        <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-border/50">
                          <span>{formatDate(post.date)}</span>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{post.readTime} min read</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ─── Newsletter CTA ─── */}
      <section className="py-24 relative bg-muted/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6">
              <Mail className="w-4 h-4 text-primary" />
              <span className="text-sm">Newsletter</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Stay in the Loop
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Get the latest property insights, market updates, and expert tips
              delivered straight to your inbox.
            </p>
            <form
              onSubmit={handleSubscribe}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1"
              />
              <Button type="submit" className="px-8">
                Subscribe
              </Button>
            </form>
            <p className="text-xs text-muted-foreground mt-4">
              No spam, unsubscribe anytime.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
